import { RecentlyViewed } from './recently-viewed-products.js';

/**
 * Recently Viewed Products Section Component
 * 
 * A custom element that displays recently viewed products using localStorage.
 * Combines Prestige's functionality with Horizon's layout patterns.
 */
class RecentlyViewedProductsSection extends HTMLElement {
  /** @type {boolean} */
  #isLoaded = false;

  constructor() {
    super();
  }

  connectedCallback() {
    // Only load products once when the element is connected to the DOM
    if (!this.#isLoaded) {
      this.loadProducts();
    }
  }

  /**
   * Gets the search query string for recently viewed products
   * @returns {string} The search query string
   */
  #getSearchQueryString() {
    const viewedProducts = RecentlyViewed.getProducts();
    const maxProducts = parseInt(this.getAttribute('products-count') || '8');
    const excludeId = this.getAttribute('exclude-id');
    
    let filteredProducts = viewedProducts;
    
    // Exclude current product if specified
    if (excludeId) {
      filteredProducts = viewedProducts.filter(id => id !== excludeId);
    }
    
    // Limit to max products and create search query
    const productIds = filteredProducts.slice(0, maxProducts);
    
    if (productIds.length === 0) {
      return '';
    }
    
    return productIds.map(id => `id:${id}`).join(' OR ');
  }

  /**
   * Loads recently viewed products from the server
   */
  async loadProducts() {
    if (this.#isLoaded) {
      return;
    }

    const searchQuery = this.#getSearchQueryString();
    
    if (!searchQuery) {
      // No products to show, hide the section
      this.closest('.shopify-section')?.remove();
      return;
    }

    this.#isLoaded = true;

    try {
      const section = this.closest('.shopify-section');
      const sectionId = section?.id.replace('shopify-section-', '') || '';
      
      const shopify = /** @type {any} */ (window.Shopify);
      const rootPath = shopify && shopify.routes ? shopify.routes.root : '/';
      const url = `${rootPath}search?type=product&q=${encodeURIComponent(searchQuery)}&section_id=${sectionId}`;
      
      const response = await fetch(url, { 
        priority: 'low',
        headers: {
          'Accept': 'text/html'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      const newRecentlyViewedElement = tempDiv.querySelector('recently-viewed-products');
      
      if (newRecentlyViewedElement && newRecentlyViewedElement.children.length > 0) {
        // Replace content with server-rendered products
        this.innerHTML = newRecentlyViewedElement.innerHTML;
        
        // Dispatch custom event for other components that might need to know
        this.dispatchEvent(new CustomEvent('recently-viewed:loaded', {
          bubbles: true,
          detail: { 
            productCount: newRecentlyViewedElement.children.length 
          }
        }));
      } else {
        // No products found, remove the section
        section?.remove();
      }
    } catch (error) {
      console.warn('Failed to load recently viewed products:', error);
      // On error, hide the section gracefully
      this.closest('.shopify-section')?.remove();
    }
  }

  /**
   * Refresh the recently viewed products
   * Useful when products are added/removed from the viewed list
   */
  refresh() {
    this.#isLoaded = false;
    this.innerHTML = '';
    this.loadProducts();
  }
}

// Register the custom element if not already registered
if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProductsSection);
}

/**
 * Add product to recently viewed when on product pages
 */
document.addEventListener('DOMContentLoaded', () => {
  // Only run on product pages
  if (document.body.classList.contains('template-product')) {
    const meta = /** @type {any} */ (window).meta;
    const productId = meta && meta.product ? meta.product.id : null;
    
    if (productId) {
      RecentlyViewed.addProduct(productId.toString());
      
      // Refresh any recently viewed sections on the page
      document.querySelectorAll('recently-viewed-products').forEach(section => {
        if (section instanceof RecentlyViewedProductsSection && typeof section.refresh === 'function') {
          section.refresh();
        }
      });
    }
  }
});

export { RecentlyViewedProductsSection };
 